import { createContext, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldAlert, ShieldCheck, Loader2, Play, Check, X } from 'lucide-react';
import { superAdminApi } from '../../api/superAdmin.js';

/**
 * ActionProposalCard — 超管 AI 對話的「提案 → 人工確認 → 執行」卡片
 *
 * AI 依 admin-scope 規範輸出 ```action-proposal fenced JSON；MarkdownText 偵測該語言
 * 標記後渲染本卡片。按「確認執行」：
 *   - kind=http  → superAdminApi.executeProposal()（走 admin-console /api proxy，帶超管身分）
 *   - kind=skill → 透過 context 的 sendMessage 把 `[action-confirmed]` 回灌對話，AI 才執行
 * 執行結果（成功/失敗）會以訊息回灌對話，讓 AI 接續。
 *
 * 無 Provider（非超管 context，例如一般 builder 聊天誤渲染）→ 顯示唯讀卡，無執行按鈕。
 */
export const ActionProposalContext = createContext(null);

const RISK_STYLE = {
    high: 'border-danger-500/40 bg-danger-100/30',
    medium: 'border-warn-500/40 bg-warn-100/20',
    low: 'border-cream-300 bg-cream-50',
};

export function ActionProposalCard({ raw }) {
    const { t } = useTranslation('superAdmin');
    const ctx = useContext(ActionProposalContext);
    const [state, setState] = useState('idle');   // idle | running | done | failed
    const [resultMsg, setResultMsg] = useState('');

    let proposal = null;
    try { proposal = JSON.parse(raw); } catch { /* 非法 JSON → 原樣顯示 */ }
    if (!proposal || !proposal.action) {
        return <pre className="my-2 p-3 rounded-md bg-cream-100 text-xs overflow-x-auto">{raw}</pre>;
    }

    const risk = proposal.risk || 'medium';
    const planned = proposal.status === 'planned';

    const run = async () => {
        if (!ctx || state === 'running' || state === 'done') return;
        setState('running');
        try {
            if ((proposal.kind || 'http') === 'http') {
                const result = await superAdminApi.executeProposal(proposal);
                setState('done');
                setResultMsg(t('proposal.executed'));
                ctx.onExecuted?.(proposal, result);
            } else {
                // kind=skill → 回灌確認訊息，AI 端執行
                ctx.sendMessage?.(`[action-confirmed] ${proposal.action}：${proposal.title}\n${JSON.stringify(proposal)}`,
                    { displayText: `✅ ${t('proposal.confirmed')}：${proposal.title}` });
                setState('done');
                setResultMsg(t('proposal.confirmSent'));
            }
        } catch (e) {
            setState('failed');
            setResultMsg(e.message || String(e));
            ctx.onExecuted?.(proposal, { error: e.message });
        }
    };

    return (
        <div className={`my-3 rounded-lg border p-4 max-w-full overflow-hidden whitespace-normal font-sans ${RISK_STYLE[risk] || RISK_STYLE.medium}`}>
            <div className="flex items-start gap-2.5">
                {risk === 'high'
                    ? <ShieldAlert size={18} className="text-danger-500 shrink-0 mt-0.5" />
                    : <ShieldCheck size={18} className="text-accent-500 shrink-0 mt-0.5" />}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-ink-900">{proposal.title || proposal.action}</span>
                        <span className="font-mono text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded bg-cream-200 text-ink-500">{proposal.action}</span>
                        {proposal.region && <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-cream-200 text-ink-500">{proposal.region}</span>}
                        <span className={`font-mono text-[10px] uppercase px-1.5 py-0.5 rounded ${risk === 'high' ? 'bg-danger-100 text-danger-500' : 'bg-cream-200 text-ink-500'}`}>{t(`proposal.risk.${risk}`, { defaultValue: risk })}</span>
                    </div>
                    {proposal.summary && <p className="text-sm text-ink-700 mt-1.5 leading-relaxed break-words">{proposal.summary}</p>}
                    {Array.isArray(proposal.preChecks) && proposal.preChecks.length > 0 && (
                        <ul className="mt-1.5 space-y-0.5">
                            {proposal.preChecks.map((c, i) => (
                                <li key={i} className="text-xs text-ink-500 flex items-start gap-1"><Check size={12} className="mt-0.5 shrink-0 text-success-500" />{c}</li>
                            ))}
                        </ul>
                    )}
                    <details className="mt-2">
                        <summary className="text-[11px] text-ink-400 cursor-pointer select-none">{t('proposal.detail')}</summary>
                        <pre className="mt-1 p-2 rounded bg-cream-100 text-[11px] overflow-x-auto max-h-48">{JSON.stringify({ method: proposal.method, endpoint: proposal.endpoint, params: proposal.params, skillCommand: proposal.skillCommand }, null, 2)}</pre>
                    </details>
                    <div className="mt-3 flex items-center gap-3">
                        {ctx && !planned && state !== 'done' && (
                            <button
                                onClick={run}
                                disabled={state === 'running'}
                                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md bg-accent-500 text-cream-50 text-sm font-medium hover:bg-accent-700 disabled:opacity-60 transition"
                            >
                                {state === 'running' ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                                {state === 'running' ? t('proposal.running') : t('proposal.confirm')}
                            </button>
                        )}
                        {planned && <span className="text-xs text-ink-400 italic">{t('proposal.planned')}</span>}
                        {state === 'done' && <span className="text-sm text-success-500 inline-flex items-center gap-1"><Check size={14} />{resultMsg}</span>}
                        {state === 'failed' && <span className="text-sm text-danger-500 inline-flex items-center gap-1"><X size={14} />{resultMsg}</span>}
                    </div>
                </div>
            </div>
        </div>
    );
}
